import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

const SidebarNav = ({ pages, onClose }) => {
  const theme = useTheme();
  const [activeLink, setActiveLink] = useState('');
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    const currentPath = window && window.location ? window.location.pathname : '';

    setActiveLink(currentPath);
    setOpenGroups({
      portfolio: currentPath.startsWith('/portfolio'),
    });
  }, []);

  const handleGroupToggle = (groupTitle) => {
    setOpenGroups((prevState) => ({
      ...prevState,
      [groupTitle.toLowerCase()]: !prevState[groupTitle.toLowerCase()],
    }));
  };

  return (
    <Box>
      <Box
        display={'flex'}
        justifyContent={'flex-end'}
        onClick={() => onClose()}
      >
        <IconButton>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box paddingX={2} paddingBottom={2}>
        <Box>
          {pages.map((item, i) => (
            <Box key={i} marginBottom={4}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  marginBottom: 1,
                  display: 'block',
                }}
              >
                {item.title}
              </Typography>
              <Grid container spacing={1}>
                {item.pages.map((p, i) => {
                  const hasChildren = Array.isArray(p.children) && p.children.length > 0;
                  const isOpen = Boolean(openGroups[p.title.toLowerCase()]);

                  return (
                    <Grid item xs={12} key={i}>
                      {hasChildren ? (
                        <Box>
                          <Button
                            color={'inherit'}
                            fullWidth
                            onClick={() => handleGroupToggle(p.title)}
                            sx={{
                              justifyContent: 'space-between',
                              paddingX: 0,
                              paddingY: 1,
                              color: activeLink.startsWith(p.href)
                                ? 'primary.main'
                                : 'text.primary',
                              fontWeight: activeLink.startsWith(p.href) ? 700 : 500,
                              textTransform: 'none',
                            }}
                          >
                            <Box textAlign={'left'}>
                              <Typography variant={'body1'}>{p.title}</Typography>
                            </Box>
                            <ExpandMoreIcon
                              sx={{
                                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                              }}
                            />
                          </Button>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box
                              marginTop={1}
                              paddingLeft={2}
                              borderLeft={`1px solid ${theme.palette.divider}`}
                            >
                              {p.children.map((child) => (
                                <Link
                                  key={child.href}
                                  variant="body2"
                                  component={'a'}
                                  href={child.href}
                                  color={activeLink === child.href ? 'primary' : 'textPrimary'}
                                  underline={'none'}
                                  display={'block'}
                                  sx={{
                                    paddingY: 1,
                                    '&:hover': {
                                      textDecoration: 'none',
                                      color: theme.palette.primary.dark,
                                    },
                                  }}
                                  onClick={() => onClose()}
                                >
                                  <Typography variant={'subtitle2'}>
                                    {child.title}
                                  </Typography>
                                  <Typography variant={'caption'} color={'textSecondary'}>
                                    {child.eyebrow}
                                  </Typography>
                                </Link>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      ) : (
                        <Link
                          variant="body2"
                          component={'a'}
                          href={p.href}
                          color={activeLink === p.href ? 'primary' : 'textPrimary'}
                          underline={'none'}
                          sx={{
                            fontWeight: activeLink === p.href ? 600 : 400,
                            display: 'block',
                            paddingY: 1,
                            '&:hover': {
                              textDecoration: 'none',
                              color: theme.palette.primary.dark,
                            },
                          }}
                          onClick={() => onClose()}
                        >
                          {p.title}
                        </Link>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Box>
        <Box>
          <Button
            variant="outlined"
            fullWidth
            component="a"
            href="/"
          >
            Home
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

SidebarNav.propTypes = {
  pages: PropTypes.array.isRequired,
  onClose: PropTypes.func,
};

export default SidebarNav;
